import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import RichTextEditor from '../components/RichTextEditor';
import { toast } from 'sonner';
import { ArrowLeft, MessageSquare, Paperclip, Send, Loader2, Download, Bot } from 'lucide-react';
import { format } from 'date-fns';

const ClientTicketDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Reopen states
  const [showReopenDialog, setShowReopenDialog] = useState(false);
  const [reopenText, setReopenText] = useState('');
  const [submittingReopen, setSubmittingReopen] = useState(false);
  
  const navigate = useNavigate();

  const handleReopenSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!reopenText.trim()) {
      toast.error('Reopen explanation is required');
      return;
    }
    setSubmittingReopen(true);
    try {
      await api.put(`/tickets/${id}`, { status: 'Open', reopen_text: reopenText });
      setShowReopenDialog(false);
      setReopenText('');
      // Refresh details and comments (since a public comment is posted)
      fetchTicketDetails();
      fetchComments();
      toast.success('Ticket reopened successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen ticket');
    } finally {
      setSubmittingReopen(false);
    }
  };

  useEffect(() => {
    fetchTicketDetails();
    fetchComments();
    fetchAttachments();
    
    // Listen for ticket updates via WebSocket notifications
    const handleTicketUpdate = (event) => {
      if (event.detail.ticketId === parseInt(id)) {
        console.log('Ticket updated, refreshing details...');
        fetchTicketDetails();
        fetchComments();
        fetchAttachments();
        toast.info('Ticket updated', { duration: 2000 });
      }
    };
    
    window.addEventListener('ticket-updated', handleTicketUpdate);
    
    return () => {
      window.removeEventListener('ticket-updated', handleTicketUpdate);
    };
  }, [id]);

  const fetchTicketDetails = async () => {
    try {
      const response = await api.get(`/tickets/${id}`);
      setTicket(response.data);
    } catch (error) {
      toast.error('Failed to fetch ticket details');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await api.get(`/tickets/${id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchAttachments = async () => {
    try {
      const response = await api.get(`/tickets/${id}/attachments`);
      setAttachments(response.data);
    } catch (error) {
      console.error('Error fetching attachments:', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    const plainText = newComment ? newComment.replace(/<[^>]+>/g, '').trim() : '';
    if (!plainText) {
      toast.error('Comment cannot be empty');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/tickets/${id}/comments`, { comment: newComment, is_internal: false });
      setComments([...comments, response.data]);
      setNewComment('');
      toast.success('Comment added successfully');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/tickets/${id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAttachments([...attachments, response.data]);
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <p className="text-lg text-slate-600 mb-4">Ticket not found</p>
        <Button onClick={() => navigate('/tickets')} className="bg-indigo-600 hover:bg-indigo-700">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Tickets
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              {ticket.title}
            </h1>
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                variant="ghost"
                onClick={() => navigate('/tickets')}
                className="text-slate-600 hover:text-slate-900 -ml-3 px-2"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <span className="text-sm font-mono text-indigo-600 font-bold" data-testid="ticket-id">
                {ticket.ticket_id}
              </span>
              <Badge className={getStatusColor(ticket.status)}>
                {ticket.status?.replace('_', ' ')}
              </Badge>
              {(ticket.status === 'Resolved' || ticket.status === 'Closed' || ticket.status === 'RESOLVED' || ticket.status === 'CLOSED') && (
                <Button
                  onClick={() => {
                    setReopenText('');
                    setShowReopenDialog(true);
                  }}
                  size="sm"
                  className="bg-orange-600 hover:bg-orange-700 text-white font-semibold flex items-center gap-1.5 h-7 px-3 py-1 rounded-md text-xs"
                >
                  <Bot size={12} />
                  Reopen Ticket
                </Button>
              )}
              <span className="text-sm text-slate-500">
                Created {format(new Date(ticket.created_at), 'MMM dd, yyyy')}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-900/80 shadow-sm bg-white">
            <CardHeader>
              <CardTitle className="text-lg dark:text-white">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <RichTextEditor value={ticket.description} readOnly className="prose max-w-none dark:prose-invert" />
            </CardContent>
          </Card>

          {/* Comments Section */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-900/80 shadow-sm bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg dark:text-white">
                <MessageSquare size={20} />
                Comments ({comments.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {comments.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-8">No comments yet. Be the first to comment!</p>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="flex gap-3 p-4 border border-slate-200 dark:border-slate-700 dark:bg-slate-800/50 rounded-xl bg-white" data-testid={`comment-${comment.id}`}>
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-indigo-500 text-white text-sm">
                        {comment.user?.full_name?.charAt(0) || comment.user?.username?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm dark:text-white">{comment.user?.full_name || comment.user?.username}</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">{format(new Date(comment.created_at), 'MMM dd, yyyy HH:mm')}</span>
                        {comment.is_internal && (
                          <Badge variant="secondary" className="bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 text-xs">
                            Internal
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-slate-700">
                        <RichTextEditor value={comment.comment} readOnly className="prose max-w-none" />
                      </div>
                    </div>
                  </div>
                ))
              )}

              <form onSubmit={handleCommentSubmit} className="space-y-3" data-testid="comment-form">
                <div data-testid="comment-input">
                  <RichTextEditor
                    value={newComment}
                    onChange={(value) => setNewComment(value)}
                    placeholder="Add a comment..."
                  />
                </div>
                <Button type="submit" data-testid="submit-comment-btn" disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                  Add Comment
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Attachments Section */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-900/80 shadow-sm bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg dark:text-white">
                <Paperclip size={20} />
                Attachments ({attachments.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {attachments.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-8">No attachments yet</p>
              ) : (
                attachments.map((attachment) => (
                  <div key={attachment.id} className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-700 dark:bg-slate-800/50 rounded-lg bg-slate-50 dark:bg-slate-800">
                    <div className="flex items-center gap-3">
                      <Paperclip size={16} className="text-slate-500 dark:text-slate-400" />
                      <div>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{attachment.filename}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Uploaded {format(new Date(attachment.created_at), 'MMM dd, yyyy')}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.open(attachment.file_url, '_blank')}
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                    >
                      <Download size={16} />
                    </Button>
                  </div>
                ))
              )}

              <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
                <label htmlFor="file-upload" className="cursor-pointer">
                  <div className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300">
                    <Paperclip size={16} />
                    <span>{uploadingFile ? 'Uploading...' : 'Attach a file'}</span>
                  </div>
                  <input
                    id="file-upload"
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploadingFile}
                  />
                </label>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Details Card */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-900/80 shadow-sm bg-white">
            <CardHeader>
              <CardTitle className="text-lg dark:text-white">Ticket Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Product</p>
                <p className="font-medium dark:text-white">{ticket.product?.name}</p>
              </div>
              
              <div>
                <p className="text-xs text-slate-500 mb-1">Category</p>
                <p className="font-medium">{ticket.category?.name || 'N/A'}</p>
              </div>
              
              {ticket.subcategory && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Subcategory</p>
                  <p className="font-medium">{ticket.subcategory.name}</p>
                </div>
              )}
              
              <div>
                <p className="text-xs text-slate-500 mb-1">Issue Type</p>
                <p className="font-medium">{ticket.issue_type?.name || 'N/A'}</p>
              </div>

              <div>
                <p className="text-xs text-slate-500 mb-1">Created By</p>
                <p className="font-medium">{ticket.creator?.full_name || ticket.creator?.username}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Reopen Ticket Dialog */}
      <Dialog open={showReopenDialog} onOpenChange={setShowReopenDialog}>
        <DialogContent className="bg-white dark:bg-slate-900 dark:border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
              <Bot className="h-5 w-5" />
              Reopen Ticket
            </DialogTitle>
            <DialogDescription>
              Please describe why you are reopening this ticket. This will be posted as a public comment.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleReopenSubmit} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="reopen-text" className="text-sm font-semibold">Reopen Explanation *</Label>
              <textarea
                id="reopen-text"
                value={reopenText}
                onChange={(e) => setReopenText(e.target.value)}
                placeholder="Explain what needs further attention..."
                rows={4}
                className="w-full min-h-[100px] p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-white text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowReopenDialog(false)}
                disabled={submittingReopen}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submittingReopen || !reopenText.trim()}
                className="bg-orange-600 hover:bg-orange-700 text-white font-semibold"
              >
                {submittingReopen ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Reopening...
                  </>
                ) : (
                  'Reopen Ticket'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientTicketDetail;
